import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  apercuEnvoiContrat,
  deleteContrat,
  envoyerContrat,
  getContrats,
  telechargerPdfContrat,
} from '../services/contratService';
import ApercuEmail from './ApercuEmail';
import ActionsLigne from '../components/ActionsLigne';
import ConfirmationSuppression from '../components/ConfirmationSuppression';
import GenererSuivant from '../components/GenererSuivant';
import { formatDate, formatMontant } from './devisUtils';
import ContratPanneau from './ContratPanneau';
import CreerDepuisTextePanel from './CreerDepuisTextePanel';
import ModeleEmailPanneau from './ModeleEmailPanneau';
import SessionsPanel from './SessionsPanel';
import Parapheur from './Parapheur';
import BoutonExport from './BoutonExport';
import FiltreDossier from './FiltreDossier';
import StatutsBarre from './StatutsBarre';
import { FACETTES_CONTRAT, FACETTES_ORIGINE, FACETTES_PLANIFICATION } from './facettes';
import FiltresRepliables from '../components/FiltresRepliables';
import '../assets/styles/Devis.css';
import '../assets/styles/DemandeDevis.css';
import '../assets/styles/Listes.css';
import { FlashToast } from '../components/Toasts';
import { IconeOeil, IconeTelecharger } from '../components/IconesDocument';

const LIMIT = 15;

/**
 * Le contrat n'a pas de colonne de statut : son avancement se lit dans le
 * couple (signature_organisme_le, signature_client_le). Une seule fonction
 * pour la pastille et son libellé, afin que les deux ne divergent pas.
 */
function etatSignature(c) {
  // Conclu ailleurs (dossier direct) : contrat en place, aucune signature ici.
  if (c.etat === 'deja_conclu') {
    return {
      libelle: 'Déjà conclu',
      classe: ' devis-pill-valide',
      detail: 'conclu ailleurs · repris tel quel, sans envoi ni signature ici',
    };
  }
  if (c.etat === 'contrat_signe') {
    return {
      libelle: 'Contrat accepté et signé',
      classe: ' devis-pill-valide',
      detail: `signé le ${formatDate(c.signature_client_le)}`,
    };
  }
  if (c.etat === 'envoye') {
    return {
      libelle: 'Contrat envoyé',
      classe: ' devis-pill-attente',
      detail: `envoyé le ${formatDate(c.date_envoi)} · en attente du client`,
    };
  }
  // Validé côté organisme et pas encore parti. La planification n'entre plus
  // dans ce compte : elle a sa propre pastille (cf. etatPlanification).
  if (c.etat === 'a_envoyer') {
    return {
      libelle: 'Contrat à envoyer',
      classe: ' devis-pill-attente',
      detail: 'en attente d’envoi',
    };
  }
  return {
    libelle: 'Contrat à valider',
    classe: '',
    detail: c.devis_signature_le
      ? `devis signé le ${formatDate(c.devis_signature_le)}`
      : 'aucune signature',
  };
}

/**
 * L'autre pastille de la ligne : où en sont les journées.
 *
 * Elle ne dit rien des signatures — un contrat signé mais non planifié
 * affiche les deux, et c'est bien ce qu'on veut voir.
 */
function etatPlanification(c) {
  if (c.etat_planification === 'planifie') {
    return {
      libelle: 'Planifié',
      classe: ' devis-pill-valide',
      detail: `${c.nb_journees} journée${c.nb_journees > 1 ? 's' : ''}`,
    };
  }
  return {
    libelle: 'À planifier',
    classe: ' devis-pill-refuse',
    detail: c.nb_journees
      ? `${c.journees_incompletes} journée(s) sans tous ses documents`
      : 'aucune journée planifiée',
  };
}

function ListeContrats({ onImporter }) {
  const [contrats, setContrats] = useState([]);
  const importRef = useRef(null);
  // Réglage de l'email propre à cette page (cf. ModeleEmailPanneau).
  const [panneauEmail, setPanneauEmail] = useState(false);
  // « Créer un contrat » depuis un texte collé.
  const [creerOuvert, setCreerOuvert] = useState(false);
  // Planification du contrat : sessions, participants, documents.
  const [contratOuvert, setContratOuvert] = useState(null);
  // Contrat affiché dans le panneau d'aperçu (bouton « Voir »).
  const [contratVu, setContratVu] = useState(null);
  const [suppression, setSuppression] = useState(null);
  const [suppressionEnCours, setSuppressionEnCours] = useState(false);

  async function handleSupprimer() {
    if (!suppression) return;
    const cible = suppression;
    setSuppressionEnCours(true);
    try {
      const res = await deleteContrat(cible.id);
      const b = res.data || {};
      setFlash(
        `Contrat ${cible.numero} supprimé, avec ${b.journees ?? 0} journée(s) et ${b.participants ?? 0} participant(s). Le devis reste.`
      );
      setSuppression(null);
      charger();
    } catch (err) {
      setLigneErreur({ id: cible.id, message: err.message || 'Suppression impossible.' });
      setSuppression(null);
    } finally {
      setSuppressionEnCours(false);
    }
  }
  const [total, setTotal] = useState(0);
  const [compteurs, setCompteurs] = useState({});
  const [page, setPage] = useState(1);

  // Dossier d'où l'on vient (boutons d'accès d'une demande, cf. Avancement) :
  // `cibleContact` peint ses lignes en vert et ne bouge plus, `filtreContact`
  // restreint la liste et se lève depuis le bandeau.
  const [params] = useSearchParams();
  const cibleContact = Number(params.get('contact')) || null;
  const cibleNom = params.get('client') || '';
  const [filtreContact, setFiltreContact] = useState(cibleContact);

  useEffect(() => {
    setFiltreContact(cibleContact);
  }, [cibleContact]);

  const [search, setSearch] = useState('');
  const [recherche, setRecherche] = useState('');
  const [signature, setSignature] = useState('');
  // Deuxième rangée de pastilles : se combine à la première.
  const [planification, setPlanification] = useState('');
  const [compteursPlanification, setCompteursPlanification] = useState({});
  // Interne (généré ici) ou externe (établi ailleurs, puis déposé au Parapheur).
  const [compteursOrigine, setCompteursOrigine] = useState({});
  const [origine, setOrigine] = useState('');
  // Période sur la date du contrat, comme les pages Demande et Devis.
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfId, setPdfId] = useState(null);
  const [envoiId, setEnvoiId] = useState(null);
  const [ligneErreur, setLigneErreur] = useState(null);
  const [flash, setFlash] = useState(null);

  // La frappe ne déclenche pas une requête par caractère.
  useEffect(() => {
    const t = setTimeout(() => {
      setRecherche(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const charger = useCallback(() => {
    setLoading(true);
    setError(null);
    getContrats({
      page,
      limit: LIMIT,
      search: recherche,
      signature,
      planification,
      origine,
      contactId: filtreContact,
      dateDebut,
      dateFin,
    })
      .then((res) => {
        setContrats(res.data || []);
        setTotal(res.pagination?.total ?? (res.data || []).length);
        setCompteurs(res.compteurs || {});
        setCompteursPlanification(res.compteursPlanification || {});
        setCompteursOrigine(res.compteursOrigine || {});
      })
      .catch((err) => setError(err.message || 'Impossible de charger les contrats.'))
      .finally(() => setLoading(false));
  }, [page, recherche, signature, planification, origine, filtreContact, dateDebut, dateFin]);

  useEffect(charger, [charger]);

  // L'aperçu de l'email avant l'envoi : le contrat en cours de relecture.
  const [apercuEnvoiContrat_, setApercuEnvoiContrat] = useState(null);

  /** Ouvre l'aperçu de l'email ; rien ne part avant le clic dans la modale. */
  function handleEnvoyer(contrat) {
    setLigneErreur(null);
    setFlash(null);
    setApercuEnvoiContrat(contrat);
  }

  /**
   * L'envoi réel, confirmé depuis l'aperçu. L'échec est affiché dans la
   * modale ; le succès sur la liste.
   */
  async function envoyerApresApercu(contrat, { sujet, texte }) {
    setEnvoiId(contrat.id);
    try {
      const res = await envoyerContrat(contrat.id, { sujet, texte });
      setFlash(res.message || `Contrat ${contrat.numero} envoyé.`);
      charger();
    } finally {
      setEnvoiId(null);
    }
  }

  async function handlePdf(id) {
    setPdfId(id);
    setLigneErreur(null);
    try {
      await telechargerPdfContrat(id);
    } catch (err) {
      setLigneErreur({ id, message: err.message || 'Téléchargement impossible.' });
    } finally {
      setPdfId(null);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const rangeStart = total === 0 ? 0 : (page - 1) * LIMIT + 1;
  const rangeEnd = Math.min(page * LIMIT, total);
  const filtreActif = Boolean(
    signature || planification || origine || dateDebut || dateFin || recherche.trim()
  );

  return (
    <div className="devis-page">
      <div className="devis-header">
        <div>
          <h1 className="devis-title">Contrat</h1>
        </div>
        <div className="devis-header-actions">
          <BoutonExport
            chemin="/contrats/export"
            filtres={{
              search: recherche,
              contactId: filtreContact,
              signature,
              origine,
              dateDebut,
              dateFin,
            }}
          />
          <button
            type="button"
            className="devis-btn"
            onClick={() => setPanneauEmail(true)}
            title="Objet et contenu de l'email envoyé au client depuis cette page"
          >
            Paramétrage email
          </button>
          <button
            type="button"
            className="devis-btn"
            onClick={() => importRef.current?.click()}
            title="Importer des contrats établis hors de l’application (plusieurs fichiers à la fois) : ils arrivent dans le Parapheur"
          >
            Importer
          </button>
          <input
            ref={importRef}
            type="file"
            hidden
            multiple
            accept=".pdf,.doc,.docx,.odt,.jpg,.jpeg,.png,.webp"
            onChange={(e) => {
              const choisis = Array.from(e.target.files || []);
              e.target.value = '';
              if (choisis.length) onImporter(choisis);
            }}
            aria-label="Importer des contrats"
          />
          <button
            type="button"
            className="devis-btn devis-btn-primary"
            onClick={() => setCreerOuvert(true)}
            title="Créer un contrat depuis un texte collé (mail, devis, convention)"
          >
            Créer un contrat
          </button>
        </div>
      </div>

      <FiltreDossier
        nom={cibleNom}
        actif={Boolean(filtreContact)}
        total={total}
        onVoirTout={() => {
          setFiltreContact(null);
          setPage(1);
        }}
      />

      <FiltresRepliables libelle="Filtres">
        <div className="devis-toolbar">
          <input
            type="text"
            className="devis-search"
            placeholder="Rechercher un numéro, un client..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className="devis-date-filter">
            <div className="devis-periode">
              <label className="devis-date-field">
                <span>Du</span>
                <input
                  type="date"
                  value={dateDebut}
                  onChange={(e) => {
                    setDateDebut(e.target.value);
                    setPage(1);
                  }}
                  max={dateFin || undefined}
                />
              </label>
              <label className="devis-date-field">
                <span>Au</span>
                <input
                  type="date"
                  value={dateFin}
                  onChange={(e) => {
                    setDateFin(e.target.value);
                    setPage(1);
                  }}
                  min={dateDebut || undefined}
                />
              </label>
            </div>
            {filtreActif && (
              <button
                type="button"
                className="devis-date-clear"
                onClick={() => {
                  setSearch('');
                  setSignature('');
                  setPlanification('');
                  setOrigine('');
                  setDateDebut('');
                  setDateFin('');
                  setPage(1);
                }}
              >
                Réinitialiser
              </button>
            )}
          </div>
          <span className="devis-total">{total} contrat{total > 1 ? 's' : ''}</span>
        </div>

        <StatutsBarre
          titre="Signature"
          options={FACETTES_CONTRAT}
          compteurs={compteurs}
          valeur={signature}
          total={Object.values(compteurs).reduce((a, n) => a + (Number(n) || 0), 0)}
          libelleTous="Tous"
          onSelect={(v) => {
            setSignature(v);
            setPage(1);
          }}
        />

        {/* Les journées, pour elles-mêmes : l'envoi du contrat ne les attend
            plus, mais la convocation et la feuille de présence en dépendent. */}
        <StatutsBarre
          titre="Planification"
          options={FACETTES_PLANIFICATION}
          compteurs={compteursPlanification}
          valeur={planification}
          total={Object.values(compteursPlanification).reduce(
            (a, n) => a + (Number(n) || 0),
            0
          )}
          libelleTous="Tous"
          onSelect={(v) => {
            setPlanification(v);
            setPage(1);
          }}
        />
        {/* Interne : généré par l'application ; externe : établi hors de
            l'application puis téléversé. Se combine avec les autres pastilles. */}
        <StatutsBarre
          titre="Origine"
          options={FACETTES_ORIGINE}
          compteurs={compteursOrigine}
          valeur={origine}
          total={Object.values(compteursOrigine).reduce((a, n) => a + (Number(n) || 0), 0)}
          libelleTous="Toutes"
          onSelect={(v) => {
            setOrigine(v);
            setPage(1);
          }}
        />

        <FlashToast texte={flash} onFermer={() => setFlash(null)} />
      </FiltresRepliables>


      <div className="devis-card">

        <div className="devis-table-wrap">
          {loading && <div className="devis-state">Chargement des contrats...</div>}

          {!loading && error && <div className="devis-state devis-state-error">{error}</div>}

          {!loading && !error && contrats.length === 0 && (
            <div className="devis-empty">
              <div className="devis-empty-title">Aucun contrat ne correspond à cette vue.</div>
              <div className="devis-empty-subtitle">
                Un contrat est créé dès qu’un client signe son devis.
              </div>
            </div>
          )}

          {!loading && !error && contrats.length > 0 && (
            <div className="demande-liste">
              {contrats.map((c) => {
                const etat = etatSignature(c);
                const planif = etatPlanification(c);
                return (
                  <article
                    key={c.id}
                    className={
                      'demande-carte' + (c.contact_id === cibleContact ? ' demande-carte-cible' : '')
                    }
                    onClick={() => setContratVu(c)}
                  >
                    <div className="demande-carte-corps">
                      {/* Qui et quoi, en une colonne : le numéro, le client,
                          l'action de formation. */}
                      <div className="demande-carte-identite">
                        <div className="demande-numero-ligne">
                          <span
                            className={`demande-numero${
                              c.signature_client_le ? ' demande-numero-valide' : ''
                            }`}
                            title={`Contrat ${c.numero}`}
                          >
                            {c.numero}
                          </span>
                          {c.devis_numero && (
                            <span className="demande-carte-ref">devis {c.devis_numero}</span>
                          )}
                          {c.origine === 'externe' && (
                            <span
                              className="badge-origine badge-origine-externe"
                              title="Contrat établi hors de l’application, puis téléversé (Parapheur)"
                            >
                              Externe
                            </span>
                          )}
                        </div>

                        <div className="devis-contact-name">{c.societe_nom || '—'}</div>
                        <div className="demande-carte-coordonnees">
                          <span>{[c.prenom, c.nom].filter(Boolean).join(' ')}</span>
                          {c.contact_email && <span>{c.contact_email}</span>}
                        </div>

                        <div className="demande-carte-societe">
                          <span className="liste-clamp" title={c.intitule_action || ''}>
                            {c.intitule_action || '—'}
                          </span>
                        </div>
                        <div className="devis-status-detail">
                          {[
                            c.dates_session_texte,
                            c.nb_participants ? `${c.nb_participants} participant(s)` : null,
                          ]
                            .filter(Boolean)
                            .join(' · ') || '—'}
                        </div>
                      </div>

                      <div className="demande-carte-etat">
                        <span className={`devis-status-pill${etat.classe}`}>{etat.libelle}</span>
                        <div className="devis-status-detail">{etat.detail}</div>
                        {/* Les journées, sous la signature : deux dimensions
                            indépendantes, deux pastilles. La planification ne
                            se montre que tant qu'elle reste à faire — une fois
                            posée, elle n'apprend plus rien. */}
                        {planif.libelle === 'À planifier' && (
                          <div className="demande-carte-pieces">
                            <span className={`devis-status-pill${planif.classe}`}>
                              {planif.libelle}
                            </span>
                            <div className="devis-status-detail">{planif.detail}</div>
                          </div>
                        )}
                        <div className="demande-carte-recu">
                          Contrat du {formatDate(c.date_contrat)}
                        </div>
                        <div className="demande-carte-montant">
                          <strong>{formatMontant(c.montant_ht)} HT</strong>
                          <span>{formatMontant(c.montant_ttc)} TTC</span>
                        </div>
                      </div>

                      {/* stopPropagation : la carte entière ouvre l'aperçu,
                          un bouton ne doit pas le faire en plus du sien. */}
                      <div className="demande-carte-actions" onClick={(e) => e.stopPropagation()}>
                        <div className="devis-actions-pile demande-actions">
                          <button
                            type="button"
                            className="devis-btn devis-btn-sm devis-btn-dossier devis-action-large"
                            onClick={() => setContratOuvert(c)}
                            title="Journées, participants, convocations et feuille de présence"
                          >
                            Planification
                          </button>
                          {/* La signature de l’organisme n’est plus requise :
                              seule celle du client l’est. Le contrat part dès
                              que la planification est faite. */}
                          {c.etat !== 'contrat_signe' && c.etat !== 'deja_conclu' && (
                            <button
                              type="button"
                              className={`devis-btn devis-btn-sm devis-action-large ${
                                c.etat === 'envoye' ? 'devis-btn-envoye' : 'devis-btn-primary'
                              }`}
                              onClick={() => handleEnvoyer(c)}
                              /* Le contrat ne part que planification faite et
                                 documents produits : la règle vit côté serveur,
                                 le bouton la montre au lieu de la faire subir. */
                              disabled={
                                envoiId === c.id || !c.nb_journees || c.journees_incompletes > 0
                              }
                              title={
                                !c.nb_journees
                                  ? 'Planifiez la formation avant l’envoi (bouton Planification)'
                                  : c.journees_incompletes > 0
                                    ? `${c.journees_incompletes} journée(s) sans tous ses documents : générez convocations et feuilles de présence`
                                    : c.etat === 'envoye'
                                      ? 'Renvoyer le contrat au client'
                                      : 'Envoyer le contrat au client pour signature'
                              }
                            >
                              {envoiId === c.id
                                ? 'Envoi'
                                : c.etat === 'envoye'
                                  ? 'Renvoyer'
                                  : 'Envoyer'}
                            </button>
                          )}
                          <div className="devis-actions-rangee">
                            <button
                              type="button"
                              className="devis-btn devis-btn-sm devis-btn-dossier"
                              onClick={() => setContratVu(c)}
                              title="Aperçu du contrat et modification de ses mentions"
                            >
                              <IconeOeil />
                              PDF
                            </button>
                            <button
                              type="button"
                              className="devis-btn devis-btn-sm devis-btn-fichier"
                              onClick={() => handlePdf(c.id)}
                              disabled={pdfId === c.id}
                            >
                              {pdfId === c.id ? (
                                'En cours'
                              ) : (
                                <>
                                  <IconeTelecharger />
                                  PDF
                                </>
                              )}
                            </button>
                          </div>
                          {/* Le prochain document : la convocation, puis la feuille
                              de présence, le contrat du formateur… au choix. */}
                          <GenererSuivant
                            devisId={c.devis_id}
                            contratId={c.id}
                            parDefaut="convocation"
                            onGenere={charger}
                          />
                          {/* Modifier et supprimer sur leur propre rangée, sous
                              voir et télécharger. */}
                          <div className="devis-actions-rangee">
                            <ActionsLigne
                              onModifier={() => setContratVu({ ...c, modeInitial: 'modification' })}
                              modifierTitre={
                                c.signature_client_le
                                  ? 'Contrat signé par le client : il ne se modifie plus'
                                  : 'Modifier les mentions et les tarifs du contrat'
                              }
                              modifierDisabled={Boolean(c.signature_client_le)}
                              onSupprimer={() => setSuppression(c)}
                              supprimerTitre="Supprimer le contrat (ses journées et documents restent)"
                              supprimerDisabled={suppressionEnCours}
                            />
                          </div>
                        </div>
                        {ligneErreur?.id === c.id && (
                          <div className="devis-row-erreur">{ligneErreur.message}</div>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>

        {!loading && !error && total > 0 && (
          <div className="devis-pagination">
            <span className="devis-pagination-info">
              {rangeStart}-{rangeEnd} sur {total}
            </span>
            <div className="devis-pagination-controls">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Précédent
              </button>
              <span className="devis-pagination-page">
                Page {page} / {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {panneauEmail && (
        <ModeleEmailPanneau type={'contrat'} onClose={() => setPanneauEmail(false)} />
      )}

      {creerOuvert && (
        <CreerDepuisTextePanel
          entite="contrat"
          onClose={() => setCreerOuvert(false)}
          onCree={(res) => {
            setFlash(res.message || 'Contrat créé.');
            charger();
          }}
        />
      )}

      {suppression && (
        <ConfirmationSuppression
          titre="Supprimer ce contrat ?"
          sousTitre={`${suppression.numero} · ${suppression.societe_nom || ''}`}
          details={['Le contrat, son PDF et ses signatures.']}
          note="Le devis, les journées, participants, convocations, feuilles de présence et émargements RESTENT (rattachés à ce contrat supprimé) ; rien n’est effacé du dossier de stockage. Restaurable depuis la corbeille."
          enCours={suppressionEnCours}
          onAnnuler={() => setSuppression(null)}
          onConfirmer={handleSupprimer}
        />
      )}

      {contratVu && (
        <ContratPanneau
          contrat={contratVu}
          modeInitial={contratVu.modeInitial || 'apercu'}
          onClose={() => setContratVu(null)}
          onModifie={() => charger()}
          onPdf={() => handlePdf(contratVu.id)}
          onEnvoyer={() => {
            setContratVu(null);
            handleEnvoyer(contratVu);
          }}
          onPlanifier={() => {
            setContratOuvert(contratVu);
            setContratVu(null);
          }}
        />
      )}

      {contratOuvert && (
        <SessionsPanel contrat={contratOuvert} onClose={() => setContratOuvert(null)} />
      )}

      {apercuEnvoiContrat_ && (
        <ApercuEmail
          titre={`Envoyer le contrat ${apercuEnvoiContrat_.numero}`}
          sousTitre={apercuEnvoiContrat_.societe_nom}
          charger={() => apercuEnvoiContrat(apercuEnvoiContrat_.id)}
          onEnvoyer={(edits) => envoyerApresApercu(apercuEnvoiContrat_, edits)}
          onClose={() => setApercuEnvoiContrat(null)}
        />
      )}
    </div>
  );
}

/**
 * Contrat : la liste des contrats, et le Parapheur — le « drive » où se déposent
 * en vrac les contrats établis hors de l'application, avant d'être
 * transformés en contrats de la liste.
 */
const ONGLETS = [
  { cle: 'contrats', label: 'Contrats' },
  { cle: 'parapheur', label: 'Parapheur' },
];

function Contrats() {
  const [params, setParams] = useSearchParams();
  const onglet = params.get('onglet') === 'parapheur' ? 'parapheur' : 'contrats';
  // Fichiers choisis depuis « Importer » de la liste : déposés par le Parapheur.
  const [aDeposer, setADeposer] = useState(null);

  function choisir(cle) {
    const suite = new URLSearchParams(params);
    if (cle === 'parapheur') suite.set('onglet', 'parapheur');
    else suite.delete('onglet');
    setParams(suite, { replace: false });
  }

  return (
    <div className="dd-conteneur">
      <nav className="cal-onglets dd-onglets" aria-label="Contrats et Parapheur">
        {ONGLETS.map((o) => (
          <button
            key={o.cle}
            type="button"
            className={'cal-onglet' + (onglet === o.cle ? ' cal-onglet-actif' : '')}
            aria-current={onglet === o.cle ? 'page' : undefined}
            onClick={() => choisir(o.cle)}
          >
            {o.label}
          </button>
        ))}
      </nav>
      <div className="dd-corps">
        {onglet === 'parapheur' ? (
          <Parapheur
            fichiersADeposer={aDeposer}
            onDepotTraite={() => setADeposer(null)}
            onContratCree={() => {
              /* le fichier passe « Transformé » : la liste des contrats se relit à son ouverture */
            }}
          />
        ) : (
          <ListeContrats
            onImporter={(fichiers) => {
              setADeposer(fichiers);
              choisir('parapheur');
            }}
          />
        )}
      </div>
    </div>
  );
}

export default Contrats;

